import { gql } from "@codesandbox/api";
import type { ComponentInput } from "@team-plain/typescript-sdk";
import { uiComponent } from "@team-plain/typescript-sdk";
import { useEditor } from "features/editor/Feature";
import { useCsbApi } from "hooks/api/useCsbApi";
import { useSession } from "hooks/api/useSession";
import {
  Box,
  Button,
  Text,
  Stack,
  Dialog,
  styled,
  Select,
  TextArea,
  Checkbox,
} from "prism-react";
import { useEffect, useState } from "react";
import UAParser from "ua-parser-js";

import { useEditorUpdate } from "hooks/useEditorUpdate";
import { FEEDBACK_EMAIL } from "utils/contact";
import { csbApi } from "utils/csbApi";

const StyledRestartStack = styled(Stack, {
  position: "relative",
  justifyContent: "space-between",
  px: "$3",
  py: "$2",
  background: "$neutral-bg-medium",
  borderRadius: "$2",
});

export const FeedbackForm: React.FC<{
  close: () => void;
}> = ({ close }) => {
  const [session] = useSession();
  const [formType, setFormType] = useState<FormType>();
  const fields = formOptions.find((option) => option.value === formType)
    ?.fields;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const getComponentsInput = useComponentsInput({ location: "editor" });
  const editor = useEditor();

  const submitDisabled = !formType || isDisabled;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSubmitting(true);

    if (!formType || session.state === "UNAUTHENTICATED") {
      return;
    }

    const priorityFormTypes: FormType[] = ["pro", "account"];
    const highPriority =
      priorityFormTypes.includes(formType) &&
      editor.subscriptionType === gql.SubscriptionType.TEAM_PRO;

    const body = {
      name: session.user.name,
      email: session.user.email,
      title: getTitle(formType),
      components: getComponentsInput(formType, event.currentTarget.elements),
      labelTypeIds: getLabelTypeIds(formType),
      priority: getPriority(
        formType,
        event.currentTarget.elements,
        highPriority,
      ),
    };

    try {
      await csbApi.post({
        path: "/support/plain/proxy",
        data: body,
      });

      setIsSuccess(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setIsError(true);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Stack gap={6}>
        <Stack css={{ justifyContent: "space-between" }} horizontal>
          <Dialog.Title>Thank you for your feedback</Dialog.Title>
          <Dialog.CloseIconButton onClick={close} />
        </Stack>

        <Dialog.Description>We respond within 72 hours.</Dialog.Description>

        <Stack css={{ justifyContent: "flex-end" }} horizontal>
          <Button
            onClick={() => {
              setIsSuccess(false);
              setIsError(false);
              setFormType(undefined);
            }}
          >
            Report another issue
          </Button>

          <Button onClick={close} variant="primary">
            Close
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={6}>
      <Stack css={{ justifyContent: "space-between" }} horizontal>
        <Dialog.Title>Submit feedback</Dialog.Title>
        <Dialog.CloseIconButton onClick={close} />
      </Stack>

      {isError && (
        <Text color="informative-error-base">
          Something went wrong. Please try again later.
        </Text>
      )}

      {/* @ts-ignore */}
      <Stack as="form" gap={6} onSubmit={onSubmit}>
        <Stack gap={3}>
          <FieldForm label="What do you need help with?">
            <Select
              onValueChange={(value) => setFormType(value as FormType)}
              placeholder="Select an option"
              value={formType}
              variant="solid"
              required
            >
              {formOptions.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select>
          </FieldForm>

          {fields && (
            <FormBuilder
              key={formType}
              fields={fields}
              setIsDisabled={setIsDisabled}
            />
          )}
        </Stack>

        <Stack css={{ justifyContent: "space-between" }} gap={2} horizontal>
          <Text color="neutral-fg-base">
            You can also email us at{" "}
            <Box as="span" css={{ color: "$neutral-fg-high" }}>
              {FEEDBACK_EMAIL}
            </Box>
          </Text>

          {!isDisabled && (
            <Button
              disabled={submitDisabled}
              loading={isSubmitting}
              variant="primary"
            >
              Send
            </Button>
          )}
        </Stack>
      </Stack>

      <UpdateEditorButton />
    </Stack>
  );
};

const FieldForm = ({
  label,
  caption,
  children,
}: React.PropsWithChildren<{ label: string; caption?: string }>) => {
  return (
    <Stack gap={2}>
      <Text>{label}</Text>
      {caption && (
        <Text color="neutral-fg-low" size="xs">
          {caption}
        </Text>
      )}
      {children}
    </Stack>
  );
};

const FormBuilder: React.FC<{
  fields: FieldTypes[];
  setIsDisabled: (value: boolean) => void;
}> = ({ fields, setIsDisabled }) => {
  const conditionalMessageIndex = fields.findIndex(
    (item) =>
      item.educationalConditionalMessage ||
      item.deleteAccountConditionalMessage,
  );
  const hasConditionalMessage = conditionalMessageIndex !== -1;
  const [educationOptionSelected, setEducationOptionSelected] = useState(false);
  const [deleteAccountSelected, setSeleteAccountSelected] = useState(false);

  useEffect(() => {
    setIsDisabled(educationOptionSelected || deleteAccountSelected);
  }, [educationOptionSelected, deleteAccountSelected]);

  return (
    <>
      {fields.map((item, index) => {
        if (
          index > conditionalMessageIndex &&
          (educationOptionSelected || deleteAccountSelected)
        ) {
          return null;
        }

        switch (item.type) {
          case "textarea":
            return (
              <FieldForm key={item.label} {...item}>
                <TextArea
                  name={item.name}
                  placeholder={item.placeholder}
                  required={item.required}
                />
              </FieldForm>
            );

          case "select":
            return (
              <FieldForm key={item.label} {...item}>
                <Select
                  name={item.name}
                  onValueChange={(value) => {
                    setEducationOptionSelected(value === "education");
                    setSeleteAccountSelected(value === "delete");
                  }}
                  placeholder={item.placeholder}
                  required={item.required}
                  variant="solid"
                >
                  {item.options.map((option) => (
                    <Select.Item
                      children={option.label}
                      key={option.value}
                      value={option.value}
                    />
                  ))}
                </Select>
              </FieldForm>
            );

          case "checkbox":
            return (
              <FieldForm key={item.label} {...item}>
                <Stack key={item.label} horizontal>
                  <div>
                    <Checkbox
                      id={item.label}
                      name={item.name}
                      required={item.required}
                    />
                  </div>
                  {/* @ts-ignore stitches error */}
                  <Text
                    as="label"
                    color="neutral-fg-low"
                    htmlFor={item.label}
                    size="xs"
                  >
                    {item.placeholder}
                  </Text>
                </Stack>
              </FieldForm>
            );
          default:
            return null;
        }
      })}

      {hasConditionalMessage && educationOptionSelected && (
        <CodeSandboxFriends />
      )}

      {hasConditionalMessage && deleteAccountSelected && <DeleteAccount />}
    </>
  );
};

const UpdateEditorButton: React.FC = () => {
  const editorUpdate = useEditorUpdate();

  const hasUpdate =
    editorUpdate?.status === "EDITOR_AND_MICRO_VM_UPDATED" ||
    editorUpdate?.status === "EDITOR_UPDATED" ||
    editorUpdate?.status === "MICRO_VM_UPDATED";

  if (hasUpdate) {
    return (
      <StyledRestartStack horizontal>
        {editorUpdate.status === "EDITOR_AND_MICRO_VM_UPDATED" ? (
          <Text>There is a new editor and microVM version available.</Text>
        ) : null}
        {editorUpdate.status === "EDITOR_UPDATED" ? (
          <Text>There is a new editor version available.</Text>
        ) : null}
        {editorUpdate.status === "MICRO_VM_UPDATED" ? (
          <Text>There is a new microVM version available.</Text>
        ) : null}

        <Button onClick={editorUpdate.restart} variant="secondary">
          Update
        </Button>
      </StyledRestartStack>
    );
  }

  return null;
};

type BaseField = {
  name: "main" | "blocker" | "topic" | "location";
  label: string;
  placeholder: string;
  caption?: string;
  required: boolean;
  educationalConditionalMessage?: true;
  deleteAccountConditionalMessage?: true;
};
type FieldTypes =
  | (BaseField & {
      type: "select";
      options: Array<{ label: string; value: string }>;
    })
  | (BaseField & {
      type: "textarea";
    })
  | (BaseField & {
      type: "checkbox";
    });

const bugReportFields: FieldTypes[] = [
  {
    label: "Where did you encounter the bug?",
    placeholder: "Bug Location",
    type: "select",
    name: "location",
    required: true,
    options: [
      { label: "Website", value: "website" },
      { label: "Dashboard", value: "dashboard" },
      { label: "Editor Interface", value: "editor" },
      { label: "Browser Sandbox", value: "sandbox" },
      { label: "Devbox", value: "devbox" },
      { label: "Repository", value: "repo" },
      { label: "Sandpack", value: "sandpack" },
      { label: "Documentation", value: "docs" },
      { label: "Other", value: "other" },
    ],
  },
  {
    type: "textarea",
    label: "What did you find?",
    required: true,
    caption:
      "The more descriptive the better. Include any relevant reproduction steps.",
    placeholder:
      "If you've encountered a bug in a specific project, please provide the full link",
    name: "main",
  },
  {
    required: false,
    type: "checkbox",
    label: "Is this preventing you from coding?",
    placeholder: "Yes, this is blocking me.",
    name: "blocker",
  },
];

const proFields: FieldTypes[] = [
  {
    type: "select",
    label: "How can we help?",
    placeholder: "Topics",
    name: "topic",
    required: true,
    options: [
      { label: "More Information", value: "know" },
      { label: "Manage Subscriptions", value: "manage" },
      { label: "Credit Utilisation", value: "credits" },
      { label: "Add-ons", value: "addons" },
      { label: "Payment Problems", value: "payments" },
      { label: "Request a Refund", value: "refund" },
      { label: "Other", value: "other" },
    ],
  },
  {
    type: "textarea",
    required: true,
    label: "Let us know how we can help",
    caption:
      "Existing subscriptions can be managed from the Dashboard Settings",
    placeholder:
      "We will need to confirm that we are speaking with the account owner before making any changes",
    name: "main",
  },
];

const accountFields: FieldTypes[] = [
  {
    type: "select",
    label: "How can we help?",
    placeholder: "Topics",
    name: "topic",
    required: true,

    // Hard-coded custom message
    deleteAccountConditionalMessage: true,
    options: [
      { label: "Login Issues", value: "login" },
      { label: "Forgot Details", value: "forget" },
      { label: "Change Email Address", value: "email" },
      { label: "Data Deletion Request", value: "data" },
      { label: "Unsubscribe from Mailing List", value: "mail" },
      { label: "Delete Account", value: "delete" },
      { label: "Other", value: "other" },
    ],
  },
  {
    name: "main",
    required: true,
    type: "textarea",
    label: "Let us know how we can help",
    caption: "You can also manage your account from the Dashboard",
    placeholder:
      "We will need to confirm that we are speaking with the account owner before making any changes",
  },
  {
    required: false,
    name: "blocker",
    type: "checkbox",
    label: "Are you unable to access your account?",
    placeholder: "Yes, I cannot login",
  },
];

const questionFields: FieldTypes[] = [
  {
    required: true,
    name: "main",
    type: "textarea",
    label: "What's on your mind?",
    placeholder: "Enter your question…",
  },
];

const featureFields: FieldTypes[] = [
  {
    required: true,
    name: "main",
    type: "textarea",
    label: "What's on your mind?",
    placeholder: "It would be great if…",
  },
];

const securityFields: FieldTypes[] = [
  {
    required: true,
    name: "main",
    type: "textarea",
    label: "What did you find?",
    caption: "We will get back to you, at the latest, in 72 hours.",
    placeholder: "When I…",
  },
];

const friendsFields: FieldTypes[] = [
  {
    required: true,
    name: "topic",
    type: "select",
    label: "What category best describes your project or organization?",
    placeholder: "Topics",

    // Hard-coded custom message
    educationalConditionalMessage: true,
    options: [
      {
        label: "Education",
        value: "education",
      },
      {
        label: "Licensed open-source software",
        value: "opensource",
      },
      {
        label: "Developer community projects",
        value: "community",
      },
      {
        label: "Non-profit organization",
        value: "nonprofit",
      },
      {
        label: "Other",
        value: "other",
      },
    ],
  },
  {
    required: true,
    name: "main",
    type: "textarea",
    label: "What company or organization do you represent?",
    placeholder:
      "Share the URL to your website and/or GitHub page and provide evidence of your ownership of the project.",
    caption: "Share all relevant links to your project and/or organization.",
  },
];

const formOptions = [
  {
    label: "Account Management",
    value: "account" as const,
    fields: accountFields,
  },
  {
    label: "Pro Subscriptions",
    value: "pro" as const,
    fields: proFields,
  },
  {
    label: "Bug report",
    value: "bug" as const,
    fields: bugReportFields,
  },
  {
    label: "CodeSandbox Friends",
    value: "friends" as const,
    fields: friendsFields,
  },
  {
    label: "Report a security issue",
    value: "security" as const,
    fields: securityFields,
  },
  {
    label: "Suggest a feature",
    value: "feature" as const,
    fields: featureFields,
  },
  {
    label: "Something else",
    value: "question" as const,
    fields: questionFields,
  },
];

type FormType = (typeof formOptions)[number]["value"];

function getTitle(formType: FormType) {
  const titleMap: Record<typeof formType, string> = {
    account: "Account management",
    pro: "Pro subscriptions",
    bug: "Bug report",
    feature: "Feature suggestion",
    question: "Question",
    security: "Security report",
    friends: "CodeSandbox Friends",
  };

  return titleMap[formType];
}

function getLabelTypeIds(formType: FormType) {
  const formTypeIdsMap: Record<typeof formType, string[]> = {
    bug: ["lt_01HB91WMYM73AD724RXSHH525P"],
    account: ["lt_01HF9E4VAJXWJ0R70RAM8KEF3G"],
    feature: ["lt_01HB91WMZ8JK0C4M11K8HQRJ25"],
    question: ["lt_01HQX3CSXNMDWTK7A1AP6572Q9"],
    security: ["lt_01HQX3BWAS8MHXXW6ZGTPEGPX8"],
    pro: ["lt_01HHMHWQDWH077ZTMVZE9AYXEX"],
    friends: ["lt_01HNWQMDQFGAE2889GNEDMMY88"],
  };

  return formTypeIdsMap[formType];
}

function getPriority(
  formType: FormType,
  fields: HTMLFormControlsCollection,
  highPriority: boolean,
) {
  if (highPriority) {
    return 0;
  }

  const namedFields = fields as unknown as Record<
    BaseField["name"],
    { value: string; checked: boolean }
  >;

  // We want to treat any security issues reported to us as urgent
  if (formType === "security") {
    return 0;
  }

  // We want to treat login issues as urgent
  if (formType === "account" && namedFields.blocker.checked) {
    return 0;
  }

  // We want to treat blocking bug reports as being high priority
  if (formType === "bug" && namedFields.blocker.checked) {
    return 0;
  }

  // We want to treat Pro requests as being high priority
  if (formType === "pro") {
    return 1;
  }

  // Default to a normal priority
  return 2;
}

const useComponentsInput = ({
  location,
}: {
  location: "editor" | "error-page";
}) => {
  const editorUpdate = useEditorUpdate();
  const [session] = useSession();
  const editor = useEditor();

  return function getComponentsInput(
    type: FormType,
    fields: HTMLFormControlsCollection,
  ): ComponentInput[] {
    const parser = new UAParser(window.navigator.userAgent);
    const browser = parser.getBrowser();
    const namedFields = fields as unknown as Record<
      BaseField["name"],
      { value: string }
    >;

    const components: ComponentInput[] = [];

    /**
     * Message structure
     * - Message
     * - User information
     * - Editor / env information
     */

    switch (type) {
      case "feature":
      case "question":
      case "security": {
        components.push(uiComponent.text({ text: namedFields.main.value }));

        break;
      }

      case "bug": {
        components.push(
          uiComponent.text({ text: namedFields.main.value }),
          uiComponent.spacer({ size: "S" }),

          uiComponent.row({
            mainContent: [
              uiComponent.text({ text: "Bug Location:", color: "NORMAL" }),
            ],
            asideContent: [
              uiComponent.text({
                text: namedFields.location.value,
                color: "MUTED",
              }),
            ],
          }),
        );

        break;
      }

      case "pro":
      case "account": {
        components.push(
          uiComponent.text({ text: namedFields.main.value }),
          uiComponent.spacer({ size: "S" }),

          uiComponent.row({
            mainContent: [
              uiComponent.text({ text: "Topic:", color: "NORMAL" }),
            ],
            asideContent: [
              uiComponent.text({
                text: namedFields.topic.value,
                color: "MUTED",
              }),
            ],
          }),
        );
        break;
      }
    }

    /**
     * User information
     */
    if (session.state === "AUTHENTICATED") {
      components.push(
        uiComponent.spacer({ size: "M" }),

        uiComponent.text({ text: "User details", color: "NORMAL" }),
        uiComponent.row({
          mainContent: [
            uiComponent.text({
              text: "User ID:",
              color: "NORMAL",
            }),
          ],
          asideContent: [
            uiComponent.text({
              text: session.user.id,
              color: "MUTED",
            }),
          ],
        }),

        uiComponent.row({
          mainContent: [
            uiComponent.text({
              text: "Workspace ID:",
              color: "NORMAL",
            }),
          ],
          asideContent: [
            uiComponent.text({
              text: editor.workspace?.id ?? "Not found",
              color: "MUTED",
            }),
          ],
        }),

        uiComponent.row({
          mainContent: [
            uiComponent.text({
              text: "Subscription:",
              color: "NORMAL",
            }),
          ],
          asideContent: [
            uiComponent.text({
              text:
                editor.subscriptionType === gql.SubscriptionType.TEAM_PRO
                  ? "Pro"
                  : "Not set",
              color:
                editor.subscriptionType === gql.SubscriptionType.TEAM_PRO
                  ? "SUCCESS"
                  : "MUTED",
            }),
          ],
        }),
      );
    }

    /**
     * Editor information
     */
    components.push(
      uiComponent.spacer({ size: "M" }),

      uiComponent.text({ text: "Editor details", color: "NORMAL" }),

      uiComponent.row({
        mainContent: [
          uiComponent.text({
            text: "Sandbox ID:",
            color: "NORMAL",
          }),
        ],
        asideContent: [
          uiComponent.text({
            text: editor.id,
            color: "MUTED",
          }),
        ],
      }),

      uiComponent.row({
        mainContent: [
          uiComponent.text({
            text: "Type:",
            color: "NORMAL",
          }),
        ],
        asideContent: [
          uiComponent.text({
            text:
              editor.type === "sandbox"
                ? editor.isCloud
                  ? "Devbox"
                  : "Sandbox"
                : "Repository",
            color: "MUTED",
          }),
        ],
      }),

      uiComponent.row({
        mainContent: [
          uiComponent.text({
            text: "Location:",
            color: "NORMAL",
          }),
        ],
        asideContent: [
          uiComponent.text({
            text: location,
            color: "MUTED",
          }),
        ],
      }),

      uiComponent.row({
        mainContent: [
          uiComponent.text({
            text: "Authenticated:",
            color: "NORMAL",
          }),
        ],
        asideContent: [
          uiComponent.text({
            text: session.state === "AUTHENTICATED" ? "Yes" : "No",
            color: session.state === "AUTHENTICATED" ? "SUCCESS" : "WARNING",
          }),
        ],
      }),

      uiComponent.row({
        mainContent: [
          uiComponent.text({
            text: "Editor is up to date:",
            color: "NORMAL",
          }),
        ],
        asideContent: [
          uiComponent.text({
            text: editorUpdate === null ? "Yes" : "No",
            color: editorUpdate === null ? "SUCCESS" : "ERROR",
          }),
        ],
      }),

      uiComponent.row({
        mainContent: [
          uiComponent.text({
            text: "Browser:",
            color: "NORMAL",
          }),
        ],
        asideContent: [
          uiComponent.text({
            text: `${browser.name} (${browser.version})`,
            color: "MUTED",
          }),
        ],
      }),

      uiComponent.row({
        mainContent: [
          uiComponent.text({
            text: "URL:",
            color: "NORMAL",
          }),
        ],
        asideContent: [
          uiComponent.text({
            text: window.location.href,
            color: "MUTED",
          }),
        ],
      }),
    );

    return components;
  };
};

const CodeSandboxFriends = () => {
  return (
    <Stack>
      <Text>Good news! We have education discounts.</Text>
      <Text color="neutral-fg-low">
        You can apply for an Education discount{" "}
        <a
          href="https://codesandbox.io/docs/learn/plans/education-plans"
          rel="noreferrer"
          target="_blank"
        >
          here.
        </a>
      </Text>
    </Stack>
  );
};

const DeleteAccount = () => {
  const csbApi = useCsbApi();
  const [doubleCheck, setDoubleCheck] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteForReal = async () => {
    try {
      await csbApi.gql.query(deleteAccountMutation, { confirm: true });

      setDeleted(true);

      /* eslint-disable-next-line */
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (deleted) {
    return (
      <Stack>
        <Text color="informative-error-subtle">
          Your account will be deleted within the next 24 hours. You can revert
          this action by accessing "User settings" in your dashboard.
        </Text>
      </Stack>
    );
  }

  if (doubleCheck) {
    return (
      <Stack>
        <Text>Account Deletion</Text>
        <Text color="neutral-fg-low" size="xs">
          Are you sure you want to delete your account?
        </Text>

        {error && <Text color="informative-error-subtle">{error}</Text>}

        <Stack horizontal>
          <Button
            css={{ backgroundColor: "$informative-error-base" }}
            onClick={deleteForReal}
            variant="primary"
          >
            Delete
          </Button>
          <Button
            onClick={() => {
              setDoubleCheck(false);
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack>
      <Text>Account deletion</Text>
      <Text color="neutral-fg-low" size="xs">
        Irreversibly delete your account and remove all your data from
        CodeSandbox.
      </Text>

      <Box>
        <Button
          css={{ backgroundColor: "$informative-error-base" }}
          onClick={() => setDoubleCheck(true)}
          variant="primary"
        >
          Request account deletion
        </Button>
      </Box>
    </Stack>
  );
};

const deleteAccountMutation = gql.createMutation(
  "DeleteCurrentUser",
  ({ confirm }: { confirm: boolean }) => ({
    deleteCurrentUser: [{ confirm }],
  }),
);
